import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";

type DocumentCategory =
  | "contract"
  | "invoice"
  | "permit"
  | "receipt"
  | "warranty"
  | "other";

interface DocumentUploaderProps {
  onUpload: (file: File, category: DocumentCategory) => Promise<void>;
  maxFileSize?: number; // in MB
  acceptedFileTypes?: string[];
}

const categoryLabels: Record<DocumentCategory, string> = {
  contract: "Contract",
  invoice: "Invoice",
  permit: "Permit",
  receipt: "Receipt",
  warranty: "Warranty",
  other: "Other",
};

const DocumentUploader: React.FC<DocumentUploaderProps> = ({
  onUpload,
  maxFileSize = 10,
  acceptedFileTypes = [".pdf", ".doc", ".docx", ".jpg", ".jpeg", ".png"],
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [category, setCategory] = useState<DocumentCategory>("other");
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setError(null);
    setUploadSuccess(false);

    if (!file) return;

    // Validate file size
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxFileSize) {
      setError(`File size must be less than ${maxFileSize}MB`);
      return;
    }

    // Validate file type
    const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
    if (!acceptedFileTypes.includes(fileExtension)) {
      setError(
        `File type not allowed. Accepted types: ${acceptedFileTypes.join(", ")}`,
      );
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError("Please select a file");
      return;
    }

    setUploading(true);
    setError(null);

    try {
      await onUpload(selectedFile, category);
      setUploadSuccess(true);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Reset success message after 3 seconds
      setTimeout(() => {
        setUploadSuccess(false);
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          Upload Document
        </Typography>

        <Stack spacing={3}>
          {/* File Input Area */}
          <Box
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: "2px dashed",
              borderColor: "primary.main",
              borderRadius: 2,
              p: 3,
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.2s",
              bgcolor: selectedFile ? "success.lighter" : "transparent",
              "&:hover": {
                bgcolor: "action.hover",
              },
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              style={{ display: "none" }}
              accept={acceptedFileTypes.join(",")}
              disabled={uploading}
            />

            {selectedFile ? (
              <>
                <CheckCircleIcon
                  sx={{ fontSize: 48, color: "success.main", mb: 1 }}
                />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  {selectedFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)}MB
                </Typography>
              </>
            ) : (
              <>
                <CloudUploadIcon
                  sx={{ fontSize: 48, color: "primary.main", mb: 1 }}
                />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Click to upload or drag and drop
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Accepted formats: {acceptedFileTypes.join(", ")}
                  <br />
                  Max size: {maxFileSize}MB
                </Typography>
              </>
            )}
          </Box>

          {/* Category Select */}
          <FormControl fullWidth>
            <InputLabel>Document Category</InputLabel>
            <Select
              value={category}
              onChange={(e) => setCategory(e.target.value as DocumentCategory)}
              label="Document Category"
              disabled={uploading}
            >
              {(Object.keys(categoryLabels) as DocumentCategory[]).map(
                (cat) => (
                  <MenuItem key={cat} value={cat}>
                    {categoryLabels[cat]}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>

          {/* Status Messages */}
          {error && <Alert severity="error">{error}</Alert>}
          {uploadSuccess && (
            <Alert severity="success">Document uploaded successfully!</Alert>
          )}

          {/* Upload Progress */}
          {uploading && (
            <Box>
              <LinearProgress />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1 }}
              >
                Uploading...
              </Typography>
            </Box>
          )}

          {/* Upload Button */}
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            fullWidth
          >
            {uploading ? <CircularProgress size={24} /> : "Upload Document"}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default DocumentUploader;
